import { ReactNode, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Settings as SettingsIcon,
  LogOut,
  Users,
  ClipboardCheck,
  ChevronLeft,
  ChevronRight,
  User,
  MessageSquare,
  Boxes,
  ListTodo,
  Network,
  Lightbulb,
  FileText,
  BookOpen,
} from 'lucide-react'
import { clsx } from 'clsx'
import { clearAuthSession, getSessionInfo } from '@/utils/auth'
import { BeaconMark, BeaconWordmark } from '@/components/BeaconBrand'
import { PlanQuotaBanner } from '@/components/PlanQuotaBanner'

interface LayoutProps {
  children: ReactNode
}

// ── Nav groups ────────────────────────────────────────────────────────────────
// `purpose` powers native title tooltips on hover (expanded + collapsed).

const BEACON_NAV = {
  label: 'Beacon',
  items: [
    {
      name: 'Chat',
      href: '/',
      icon: MessageSquare,
      purpose: 'Ask Beacon about your AWS account; it acts with your approval',
    },
    {
      name: 'Resources',
      href: '/beacon/resources',
      icon: Boxes,
      purpose: 'Ledger of resources Beacon created or tracks',
    },
    {
      name: 'Tasks',
      href: '/beacon/tasks',
      icon: ListTodo,
      purpose: 'Scheduled and one-off jobs Beacon runs',
    },
    {
      name: 'Architectures',
      href: '/beacon/architectures',
      icon: FileText,
      purpose: 'Designs Beacon drafted from chat; review them',
    },
    {
      name: 'VPC',
      href: '/beacon/vpc',
      icon: Network,
      purpose: 'Live view of your VPCs, subnets, and security groups',
    },
    {
      name: 'Recommendations',
      href: '/beacon/recommendations',
      icon: Lightbulb,
      purpose: 'Advisor findings and monitor results',
    },
    {
      name: 'Approvals',
      href: '/beacon/approvals',
      icon: ClipboardCheck,
      purpose: 'Pending decisions that need a human (HITL inbox)',
    },
    {
      name: 'Company',
      href: '/beacon/admin',
      icon: Users,
      purpose: 'Members, invites, and AWS account links',
    },
    {
      name: 'How To',
      href: '/beacon/howto',
      icon: BookOpen,
      purpose: 'Product process: chat → review → approve → act safely',
    },
  ],
}

const ADMIN_ITEMS = [
  {
    name: 'Users',
    href: '/users',
    icon: Users,
    purpose: 'Manage user accounts and access',
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: SettingsIcon,
    purpose: 'Models, environment, and account preferences',
  },
]

const ALWAYS_BOTTOM = [
  {
    name: 'Settings',
    href: '/settings',
    icon: SettingsIcon,
    purpose: 'Models, environment, and account preferences',
  },
]

export const Layout = ({ children }: LayoutProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(false)

  // ── Admin detection ───────────────────────────────────────────────────────
  const session = getSessionInfo()
  const isAdmin = session.isAdmin
  // Beacon-neutral shell label for DISABLE_AUTH / local sessions
  const userEmail = session.email
  const userLabel =
    userEmail === 'dev@beacon.local' ? 'Team Member'
    : userEmail === 'admin@beacon.local' || userEmail === 'owner@beacon.local' ? 'Owner'
    : userEmail === 'member@beacon.local' ? 'Team Member'
    : (userEmail.split('@')[0] || userEmail || 'Signed in')

  const beaconGroup = BEACON_NAV

  const handleLogout = () => {
    clearAuthSession()
    navigate('/login')
  }

  const sidebarW = collapsed ? 'w-16' : 'w-56'

  // Helper: render a single nav link (native title = purpose on hover)
  const NavLink = ({
    name,
    href,
    icon: Icon,
    purpose,
  }: {
    name: string
    href: string
    icon: any
    purpose: string
  }) => {
    const isActive = location.pathname === href || location.pathname.startsWith(href + '/')
    return (
      <Link
        to={href}
        title={purpose}
        aria-label={`${name}: ${purpose}`}
        className={clsx(
          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors group relative',
          isActive
            ? 'bg-beacon-orange text-beacon-navy'
            : 'text-gray-400 hover:text-white hover:bg-gray-700/60'
        )}
      >
        <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        {!collapsed && <span className="truncate">{name}</span>}
        {/* Collapsed flyout mirrors purpose (native title still works) */}
        {collapsed && (
          <span
            className="absolute left-full ml-2 max-w-xs px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity whitespace-normal"
            aria-hidden="true"
          >
            <span className="font-semibold">{name}</span>
            <span className="block font-normal text-gray-300 mt-0.5">{purpose}</span>
          </span>
        )}
      </Link>
    )
  }

  return (
    <div className="flex min-h-screen bg-gray-100">

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-30 flex flex-col bg-beacon-navy border-r border-white/10 transition-all duration-200',
          sidebarW
        )}
      >
        {/* Logo — clicking navigates to Beacon home */}
        <Link
          to="/"
          title="BeaCon home"
          className={clsx(
            'flex items-center gap-2.5 px-3 py-4 border-b border-white/10 flex-shrink-0',
            'hover:bg-white/5 transition-colors',
            collapsed && 'justify-center'
          )}
        >
          <BeaconMark size={36} className="flex-shrink-0" />
          {!collapsed && (
            <BeaconWordmark className="text-base font-bold tracking-tight truncate" textClassName="text-white" />
          )}
        </Link>

        {/* Nav groups — Beacon surfaces + Settings */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
          <div>
            {!collapsed && (
              <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
                {beaconGroup.label}
              </p>
            )}
            <div className="space-y-0.5">
              {beaconGroup.items.map(item => (
                <NavLink key={item.href} {...item} />
              ))}
            </div>
          </div>

          {/* Admin section */}
          {isAdmin && (
            <div>
              {!collapsed && (
                <p className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Admin
                </p>
              )}
              <div className="space-y-0.5">
                {ADMIN_ITEMS.map(item => (
                  <NavLink key={item.href} {...item} />
                ))}
              </div>
            </div>
          )}

          {/* Settings for non-admins */}
          {!isAdmin && (
            <div className="space-y-0.5">
              {ALWAYS_BOTTOM.map(item => (
                <NavLink key={item.href} {...item} />
              ))}
            </div>
          )}
        </nav>

        {/* User + logout */}
        <div className="border-t border-gray-700/50 px-2 py-3 flex-shrink-0">
          <button
            onClick={handleLogout}
            title={collapsed ? 'Logout' : undefined}
            className={clsx(
              'flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-gray-700/60 transition-colors group relative',
              collapsed && 'justify-center'
            )}
          >
            {userEmail && !collapsed ? (
              <>
                <User className="h-4 w-4 flex-shrink-0" />
                <span className="truncate text-xs" title={userEmail}>{userLabel}</span>
                <LogOut className="h-3.5 w-3.5 ml-auto flex-shrink-0" />
              </>
            ) : (
              <>
                <LogOut className="h-4 w-4 flex-shrink-0" />
                {collapsed && (
                  <span className="absolute left-full ml-2 px-2 py-1 text-xs bg-gray-800 text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50">
                    Logout
                  </span>
                )}
              </>
            )}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(v => !v)}
          className="absolute -right-3 top-6 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-full p-0.5 text-gray-300 hover:text-white transition-colors z-40"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed
            ? <ChevronRight className="h-3.5 w-3.5" />
            : <ChevronLeft className="h-3.5 w-3.5" />}
        </button>
      </aside>

      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <div className={clsx('flex-1 flex flex-col transition-all duration-200', collapsed ? 'ml-16' : 'ml-56')}>

        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
          <h1 className="text-sm font-semibold text-gray-500 capitalize">
            {location.pathname === '/'
              ? 'Chat'
              : location.pathname === '/beacon/howto'
                ? 'How To'
                : location.pathname.replace('/', '').replace(/-/g, ' ')}
          </h1>
          <div className="flex items-center gap-2">
            <BeaconMark size={18} />
            <BeaconWordmark className="text-xs font-semibold" textClassName="text-beacon-navy" />
          </div>
        </header>

        <PlanQuotaBanner />

        {/* Page content */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 px-6 py-3">
          <p className="text-center text-gray-400 text-xs">
            &copy; 2026 BeaCon - Personal AWS Assistant · Marketplace distributed
          </p>
        </footer>
      </div>
    </div>
  )
}
